import React, { useRef, useState } from "react";
import { Modal } from "../../../components";
import html2canvas from "html2canvas";
import { useStore } from "../../../stores";
import clsx from "clsx";
import { BsDownload } from "react-icons/bs";

interface WaFlowCustomResponseProps {
  payload: {
    flow_token: string;
    flow_id: string;
    message: string;
    flow_name: string;
    button_label: string;
    response?: {
      fieldsMap: Record<string, any>;
      completed: boolean;
    } & Record<string, any>; // dynamic flow response
  };
}

const WaFlowCustomResponseSent: React.FC<WaFlowCustomResponseProps> = ({
  payload,
}) => {
  const [viewResult, setViewResult] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const resultsRef = useRef<HTMLDivElement>(null);
  const { selectedChat } = useStore("chats");

  const downloadAsImage = async () => {
    if (!resultsRef.current) return;

    try {
      setDownloading(true);

      // Create a clone of the results div to avoid modifying the original
      const clonedResults = resultsRef.current.cloneNode(true) as HTMLElement;

      // Apply some styling for better image output
      clonedResults.style.padding = "20px";
      clonedResults.style.backgroundColor = "white";
      clonedResults.style.borderRadius = "8px";
      clonedResults.style.width = "30%";

      // Temporarily append to body but hide it
      clonedResults.style.position = "absolute";
      clonedResults.style.left = "-9999px";
      document.body.appendChild(clonedResults);

      // Generate canvas from the element
      const canvas = await html2canvas(clonedResults, {
        backgroundColor: "#ffffff",
        scale: 2, // Higher quality
        logging: false,
        useCORS: true,
      });

      // Remove the cloned element
      document.body.removeChild(clonedResults);

      // Create download link
      const link = document.createElement("a");
      link.download = `Chat-${selectedChat?.id} Flow-Result-${
        payload.flow_name || "flow"
      }.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (error) {
      console.error("Error generating image:", error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div>
      <h2 className="h-6 has-text-weight-medium has-text-white mb-1 subtitle">
        Flow Name: {payload.flow_name ?? "Custom flow"}
      </h2>
      <p>{payload.message}</p>
      <div className="is-flex is-justify-content-center my-2">
        <button
          className="button is-primary is-success is-small"
          onClick={() => setViewResult(true)}
        >
          {payload.button_label}
        </button>
      </div>
      <small>By clicking this button, you can view the flow response</small>

      {!!viewResult && (
        <Modal
          title={() => (
            <h2 className="is-flex title is-5 is-align-items-center">
              Flow Response
              <button
                className={clsx("button is-small is-ghost has-text-black", {
                  "is-loading": downloading,
                })}
                onClick={downloadAsImage}
                disabled={downloading}
              >
                <BsDownload size={14} />
              </button>
            </h2>
          )}
          isOpen
          onClose={() => setViewResult(false)}
        >
          <div className="content" ref={resultsRef}>
            {Object.entries(payload.response?.fieldsMap ?? {}).map(
              ([key, name]) => (
                <div key={key} className="mb-2">
                  <strong>{name}:</strong> <br />
                  {payload.response?.[key]}
                </div>
              )
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default WaFlowCustomResponseSent;
